import React, { useEffect, useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronRight, ShieldCheck, Package, FileText, Ruler } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type SizeOption = '60x40' | '50x25' | '40x20' | 'bespoke';
type ColourOption = 'white' | 'silver';
type MaterialOption = 'pvc' | 'polyester' | 'mark-resist-pvc';
type LaminationOption = 'no' | 'yes';
type LaminationTypeOption = 'gloss' | 'matt';
const sizeOptions = [{
  id: '60x40' as SizeOption,
  label: '60 × 40 mm'
}, {
  id: '50x25' as SizeOption,
  label: '50 × 25 mm'
}, {
  id: '40x20' as SizeOption,
  label: '40 × 20 mm'
}, {
  id: 'bespoke' as SizeOption,
  label: 'Bespoke'
}];
const colourOptions = [{
  id: 'white' as ColourOption,
  label: 'White',
  hex: '#FFFFFF'
}, {
  id: 'silver' as ColourOption,
  label: 'Silver',
  hex: '#C0C0C0'
}];
const materialOptions = [{
  id: 'pvc' as MaterialOption,
  label: 'PVC'
}, {
  id: 'polyester' as MaterialOption,
  label: 'Polyester'
}, {
  id: 'mark-resist-pvc' as MaterialOption,
  label: 'Mark resist PVC'
}];
const quantityOptions = [100, 200, 500, 1000];
// Valid variant combinations - defines what options are available based on selections
const validCombinations: Record<SizeOption, {
  colours: ColourOption[];
  materials: Record<ColourOption, MaterialOption[]>;
  lamination: Record<MaterialOption, LaminationOption[]>;
}> = {
  '60x40': {
    colours: ['white', 'silver'],
    materials: {
      white: ['pvc', 'polyester', 'mark-resist-pvc'],
      silver: ['polyester']
    },
    lamination: {
      pvc: ['no', 'yes'],
      polyester: ['no', 'yes'],
      'mark-resist-pvc': ['no']
    }
  },
  '50x25': {
    colours: ['white', 'silver'],
    materials: {
      white: ['pvc', 'polyester'],
      silver: ['polyester']
    },
    lamination: {
      pvc: ['no', 'yes'],
      polyester: ['no', 'yes'],
      'mark-resist-pvc': ['no']
    }
  },
  '40x20': {
    colours: ['white'],
    materials: {
      white: ['pvc', 'polyester'],
      silver: []
    },
    lamination: {
      pvc: ['no', 'yes'],
      polyester: ['no'],
      'mark-resist-pvc': ['no']
    }
  },
  bespoke: {
    colours: ['white', 'silver'],
    materials: {
      white: ['pvc', 'polyester', 'mark-resist-pvc'],
      silver: ['polyester']
    },
    lamination: {
      pvc: ['no', 'yes'],
      polyester: ['no', 'yes'],
      'mark-resist-pvc': ['no']
    }
  }
};
// Product images based on configuration
const productImages: Record<string, string> = {
  'white-gloss': 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?auto=format&fit=crop&q=80&w=800',
  'white-matt': 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?auto=format&fit=crop&q=80&w=800',
  'white-no': 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?auto=format&fit=crop&q=80&w=800',
  'silver-gloss': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=800',
  'silver-matt': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=800',
  'silver-no': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=800'
};
export function AssetLabelsPage() {
  const [selectedSize, setSelectedSize] = useState<SizeOption>('60x40');
  const [selectedColour, setSelectedColour] = useState<ColourOption>('white');
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialOption>('pvc');
  const [selectedLamination, setSelectedLamination] = useState<LaminationOption>('no');
  const [selectedLaminationType, setSelectedLaminationType] = useState<LaminationTypeOption>('gloss');
  const [selectedQuantity, setSelectedQuantity] = useState(100);
  useEffect(() => {
    document.title = 'Asset Labels | Industrial Labels & Nameplates';
  }, []);
  // Calculate available options based on current selection
  const availableOptions = useMemo(() => {
    const sizeConfig = validCombinations[selectedSize];
    const availableColours = sizeConfig.colours;
    const availableMaterials = sizeConfig.materials[selectedColour] || [];
    const availableLamination = sizeConfig.lamination[selectedMaterial] || ['no'];
    return {
      colours: availableColours,
      materials: availableMaterials,
      lamination: availableLamination
    };
  }, [selectedSize, selectedColour, selectedMaterial]);
  // Auto-correct selections when they become invalid
  useEffect(() => {
    if (!availableOptions.colours.includes(selectedColour)) {
      setSelectedColour(availableOptions.colours[0]);
    }
  }, [selectedSize, availableOptions.colours, selectedColour]);
  useEffect(() => {
    if (!availableOptions.materials.includes(selectedMaterial)) {
      setSelectedMaterial(availableOptions.materials[0] || 'pvc');
    }
  }, [selectedColour, availableOptions.materials, selectedMaterial]);
  useEffect(() => {
    if (!availableOptions.lamination.includes(selectedLamination)) {
      setSelectedLamination(availableOptions.lamination[0] || 'no');
    }
  }, [selectedMaterial, availableOptions.lamination, selectedLamination]);
  // Get current product image based on selection
  const getProductImage = () => {
    const laminationKey = selectedLamination === 'yes' ? selectedLaminationType : 'no';
    return productImages[`${selectedColour}-${laminationKey}`] || productImages['white-no'];
  };
  // Get selection summary
  const getSelectionSummary = () => {
    const size = sizeOptions.find(s => s.id === selectedSize)?.label;
    const colour = colourOptions.find(c => c.id === selectedColour)?.label;
    const material = materialOptions.find(m => m.id === selectedMaterial)?.label;
    const lamination = selectedLamination === 'yes' ? `${selectedLaminationType === 'gloss' ? 'Gloss' : 'Matt'} lamination` : 'No lamination';
    return {
      size,
      colour,
      material,
      lamination
    };
  };
  const summary = getSelectionSummary();
  const isColourDisabled = (colourId: ColourOption) => !availableOptions.colours.includes(colourId);
  const isMaterialDisabled = (materialId: MaterialOption) => !availableOptions.materials.includes(materialId);
  const isLaminationDisabled = (laminationId: LaminationOption) => !availableOptions.lamination.includes(laminationId);
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-351">
      <Header data-id="element-352" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-353">
        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b border-slate-100" data-id="element-354">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-3" data-id="element-355">
            <nav className="flex items-center gap-2 text-sm" data-id="element-356">
              <a href="/" className="text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-357">
                Home
              </a>
              <ChevronRight className="h-4 w-4 text-slate-300" data-id="element-358" />
              <a href="/products" className="text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-359">
                Identification Labels
              </a>
              <ChevronRight className="h-4 w-4 text-slate-300" data-id="element-360" />
              <span className="text-[#0A1628] font-medium" data-id="element-361">Asset Labels</span>
            </nav>
          </div>
        </div>

        {/* Main Product Section */}
        <section className="py-10 md:py-14" data-id="element-362">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-363">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-364">
              {/* Product Image */}
              <div className="relative" data-id="element-365">
                <motion.div key={`${selectedColour}-${selectedLamination}-${selectedLaminationType}`} initial={{
                opacity: 0.8
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.3
              }} className="aspect-square bg-slate-100 rounded-2xl overflow-hidden" data-id="element-366">
                  <img src={getProductImage()} alt="Asset Labels" className="w-full h-full object-cover" data-id="element-367" />
                </motion.div>

                {/* Image thumbnails */}
                <div className="flex gap-3 mt-4" data-id="element-368">
                  {colourOptions.map(colour => <button key={colour.id} onClick={() => !isColourDisabled(colour.id) && setSelectedColour(colour.id)} disabled={isColourDisabled(colour.id)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${selectedColour === colour.id ? 'border-[#0052CC] ring-2 ring-[#0052CC]/20' : isColourDisabled(colour.id) ? 'border-slate-100 opacity-40 cursor-not-allowed' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-369">
                      <img src={productImages[`${colour.id}-no`]} alt={`${colour.label} variant`} className="w-full h-full object-cover" data-id="element-370" />
                    </button>)}
                </div>
              </div>

              {/* Product Configuration */}
              <div data-id="element-371">
                <h1 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-3" data-id="element-372">
                  Asset Labels
                </h1>
                <p className="text-lg text-slate-500 mb-8 leading-relaxed" data-id="element-373">
                  Durable barcode, QR code, and serial number labels for fixed
                  asset tracking in industrial environments.
                </p>

                {/* Configuration Options */}
                <div className="space-y-6" data-id="element-374">
                  {/* Size */}
                  <div data-id="element-375">
                    <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-376">
                      Size
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-377">
                      {sizeOptions.map(size => <button key={size.id} onClick={() => setSelectedSize(size.id)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedSize === size.id ? 'bg-[#0052CC] text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-378">
                          {size.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Colour */}
                  <div data-id="element-379">
                    <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-380">
                      Colour
                    </label>
                    <div className="flex gap-3" data-id="element-381">
                      {colourOptions.map(colour => <button key={colour.id} onClick={() => !isColourDisabled(colour.id) && setSelectedColour(colour.id)} disabled={isColourDisabled(colour.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedColour === colour.id ? 'bg-[#0052CC] text-white shadow-md' : isColourDisabled(colour.id) ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-382">
                          <span className={`w-4 h-4 rounded-full border ${isColourDisabled(colour.id) ? 'border-slate-200' : 'border-slate-300'}`} style={{
                        backgroundColor: colour.hex
                      }} data-id="element-383" />
                          {colour.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Material */}
                  <div data-id="element-384">
                    <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-385">
                      Material
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-386">
                      {materialOptions.map(material => <button key={material.id} onClick={() => !isMaterialDisabled(material.id) && setSelectedMaterial(material.id)} disabled={isMaterialDisabled(material.id)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedMaterial === material.id ? 'bg-[#0052CC] text-white shadow-md' : isMaterialDisabled(material.id) ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-387">
                          {material.label}
                        </button>)}
                    </div>
                  </div>

                  {/* Lamination */}
                  <div data-id="element-388">
                    <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-389">
                      Lamination
                    </label>
                    <div className="flex gap-3" data-id="element-390">
                      {(['no', 'yes'] as LaminationOption[]).map(option => <button key={option} onClick={() => !isLaminationDisabled(option) && setSelectedLamination(option)} disabled={isLaminationDisabled(option)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedLamination === option ? 'bg-[#0052CC] text-white shadow-md' : isLaminationDisabled(option) ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-391">
                          {option === 'yes' ? 'Yes' : 'No'}
                        </button>)}
                    </div>
                  </div>

                  {/* Lamination Type - Only visible if lamination is Yes */}
                  <AnimatePresence data-id="element-392">
                    {selectedLamination === 'yes' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-393">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-394">
                          Lamination Type
                        </label>
                        <div className="flex gap-3" data-id="element-395">
                          {(['gloss', 'matt'] as LaminationTypeOption[]).map(type => <button key={type} onClick={() => setSelectedLaminationType(type)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedLaminationType === type ? 'bg-[#0052CC] text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-396">
                                {type === 'gloss' ? 'Gloss' : 'Matt'}
                              </button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Quantity */}
                  <div data-id="element-397">
                    <label className="block text-sm font-semibold text-[#0A1628] mb-3" data-id="element-398">
                      Quantity{' '}
                      <span className="font-normal text-slate-500" data-id="element-399">
                        (min. 100)
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-400">
                      {quantityOptions.map(qty => <button key={qty} onClick={() => setSelectedQuantity(qty)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedQuantity === qty ? 'bg-[#0052CC] text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-401">
                          {qty.toLocaleString()}
                        </button>)}
                    </div>
                  </div>
                </div>

                {/* Selection Summary */}
                <div className="mt-8 p-5 bg-slate-50 rounded-xl border border-slate-100" data-id="element-402">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-403">
                    Your selection
                  </p>
                  <div className="space-y-1" data-id="element-404">
                    <p className="text-base font-semibold text-[#0A1628]" data-id="element-405">
                      Asset Labels
                    </p>
                    <p className="text-sm text-slate-600" data-id="element-406">{summary.size}</p>
                    <p className="text-sm text-slate-600" data-id="element-407">
                      {summary.colour} {summary.material}
                    </p>
                    <p className="text-sm text-slate-600" data-id="element-408">
                      {summary.lamination}
                    </p>
                    <p className="text-sm text-slate-600" data-id="element-409">
                      Qty: {selectedQuantity.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Add to Basket */}
                <div className="mt-6" data-id="element-410">
                  <button className="w-full py-4 bg-[#0052CC] text-white font-bold text-lg rounded-xl hover:bg-[#0040A0] transition-all shadow-lg shadow-[#0052CC]/20 hover:shadow-xl hover:shadow-[#0052CC]/25" data-id="element-411">
                    Add to Basket
                  </button>
                </div>

                {/* Trust Indicators */}
                <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-500" data-id="element-412">
                  <span className="flex items-center gap-1.5" data-id="element-413">
                    <span className="text-base" data-id="element-414">🇬🇧</span>
                    UK manufactured
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-415">
                    <ShieldCheck className="h-4 w-4 text-green-600" data-id="element-416" />
                    Quality assured
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-417">
                    <Package className="h-4 w-4 text-slate-400" data-id="element-418" />
                    Samples available
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-419">
                    <Ruler className="h-4 w-4 text-slate-400" data-id="element-420" />
                    Custom sizes available
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specification Section */}
        <section className="py-12 bg-slate-50 border-t border-slate-100" data-id="element-421">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-422">
            <div className="max-w-3xl" data-id="element-423">
              <h2 className="text-2xl font-bold text-[#0A1628] mb-6" data-id="element-424">
                Technical Specification
              </h2>
              <p className="text-slate-500 mb-6" data-id="element-425">
                For complete technical specifications including material
                properties, adhesive performance, and application guidelines,
                please refer to the official technical data sheet.
              </p>
              <a href="#" className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A1628] text-white font-semibold rounded-full hover:bg-[#0A1628]/90 transition-colors" data-id="element-426">
                <FileText className="h-4 w-4" data-id="element-427" />
                View technical data sheet PDF
              </a>
            </div>
          </div>
        </section>

        {/* Custom Solution CTA */}
        <section className="py-16 bg-[#0A1628]" data-id="element-428">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center" data-id="element-429">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-430">
              Need a custom solution?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto" data-id="element-431">
              We can manufacture bespoke sizes, materials, and finishes to meet
              your exact requirements.
            </p>
            <a href="/contact" className="inline-flex items-center px-8 py-4 bg-white text-[#0A1628] font-bold rounded-full hover:bg-slate-100 transition-colors" data-id="element-432">
              Get in Touch
            </a>
          </div>
        </section>
      </main>

      <Footer data-id="element-433" />
    </div>;
}